/**
 * Homepage Sync Service
 * 
 * Automatically syncs homepage document when collections are updated
 * This ensures the homepage document stays in sync with source collections
 */

import { updateHomepageDocument } from '../utils/migrateToHomepageDocument';

// Debounce timer to prevent too many updates
let syncTimer = null;
const SYNC_DELAY = 2000; // Wait 2 seconds after last change before syncing

/**
 * Schedule a homepage document update
 * Uses debouncing to batch multiple updates
 */
export const scheduleHomepageUpdate = () => {
  // Clear existing timer
  if (syncTimer) {
    clearTimeout(syncTimer);
  }

  // Schedule update after delay
  syncTimer = setTimeout(async () => {
    try {
      console.log('🔄 Auto-updating homepage document...');
      const result = await updateHomepageDocument();
      if (result.success) {
        console.log('✅ Homepage document updated successfully');
      } else {
        console.error('❌ Failed to update homepage document:', result.error);
      }
    } catch (error) {
      console.error('❌ Error updating homepage document:', error);
    } finally {
      syncTimer = null;
    }
  }, SYNC_DELAY);
};

/**
 * Immediately update homepage document (no debouncing)
 * Use this for manual updates from admin dashboard
 */
export const updateHomepageNow = async () => {
  try {
    console.log('🔄 Updating homepage document...');
    const result = await updateHomepageDocument();
    if (result.success) {
      console.log('✅ Homepage document updated successfully');
      return { success: true, data: result.data };
    } else {
      console.error('❌ Failed to update homepage document:', result.error);
      return { success: false, error: result.error };
    }
  } catch (error) {
    console.error('❌ Error updating homepage document:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Initialize homepage sync listeners
 * Listens for collection update events and auto-syncs homepage document
 */
export const initializeHomepageSync = () => {
  if (typeof window === 'undefined') return;

  // Listen for product updates
  window.addEventListener('productsUpdated', () => {
    console.log('📦 Products updated, scheduling homepage sync...');
    scheduleHomepageUpdate();
  });

  // Listen for category updates
  window.addEventListener('categoriesUpdated', () => {
    console.log('📁 Categories updated, scheduling homepage sync...');
    scheduleHomepageUpdate();
  });

  // Listen for coupon updates (you may need to add this event)
  window.addEventListener('couponsUpdated', () => {
    console.log('🎟️ Coupons updated, scheduling homepage sync...');
    scheduleHomepageUpdate();
  });

  // Listen for announcement updates (you may need to add this event)
  window.addEventListener('announcementsUpdated', () => {
    console.log('📢 Announcements updated, scheduling homepage sync...');
    scheduleHomepageUpdate();
  });

  // Listen for founder video updates (you may need to add this event)
  window.addEventListener('founderVideosUpdated', () => {
    console.log('🎥 Founder videos updated, scheduling homepage sync...');
    scheduleHomepageUpdate();
  });

  console.log('✅ Homepage sync service initialized');
};

/**
 * Cleanup homepage sync listeners
 */
export const cleanupHomepageSync = () => {
  if (typeof window === 'undefined') return;

  // Clear any pending sync
  if (syncTimer) {
    clearTimeout(syncTimer);
    syncTimer = null;
  }

  // Remove event listeners
  window.removeEventListener('productsUpdated', scheduleHomepageUpdate);
  window.removeEventListener('categoriesUpdated', scheduleHomepageUpdate);
  window.removeEventListener('couponsUpdated', scheduleHomepageUpdate);
  window.removeEventListener('announcementsUpdated', scheduleHomepageUpdate);
  window.removeEventListener('founderVideosUpdated', scheduleHomepageUpdate);
};

